
import { useState } from "react";
import { Volume2, VolumeX, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

interface TextToSpeechProps {
  text: string;
}

export const TextToSpeech = ({ text }: TextToSpeechProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [audio, setAudio] = useState<HTMLAudioElement | null>(null);
  
  const handleSpeak = async () => {
    if (isPlaying && audio) {
      audio.pause();
      setIsPlaying(false);
      return;
    }

    try {
      setIsLoading(true);
      
      // Call our Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('text-to-speech', {
        body: {
          text,
          voice: 'nova' // One of OpenAI's high quality voices
        }
      });

      if (error) throw error;

      // Create an audio element with the returned base64 audio
      const audioElement = new Audio(`data:audio/mp3;base64,${data.audioContent}`);
      
      audioElement.onended = () => {
        setIsPlaying(false);
        setAudio(null);
      };

      setAudio(audioElement);
      await audioElement.play();
      setIsPlaying(true);
    } catch (error) {
      console.error('Error generating speech:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleSpeak}
      disabled={isLoading}
      className="flex items-center gap-2 text-purple-600 hover:text-purple-700 hover:bg-purple-100"
    >
      {isLoading ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Generating...</span>
        </>
      ) : isPlaying ? (
        <>
          <VolumeX className="w-4 h-4" />
          <span className="text-sm">Stop Reading</span>
        </>
      ) : (
        <>
          <Volume2 className="w-4 h-4" />
          <span className="text-sm">Read Aloud</span>
        </>
      )}
    </Button>
  );
};
